<script setup lang="ts">
import { twMerge } from 'tailwind-merge'
import { formatNumberFullTruncate, formatNumberSemiTruncate, formatUnshortenedNumber } from '@/helpers/utils'
import { useConfig } from '@/composables/game/config'
import { usePlayer } from '@/composables/game/player'
import { DateTime } from 'luxon'
import CoinIcon from '@/assets/images/icons/gold-coin-icon-s3.webp'
import { computed, ref, watch } from 'vue'
import { useFlyingCoins } from '@/composables/game/flying-coins'
import { useDailyReward } from '@/composables/game/daily'
import { T } from '@tolgee/vue'
import { useNow } from '@/composables/game/time'
import SpinImage from '@/assets/images/fortune-wheel.webp'
import PlusIcon from '@/components/icons/PlusIcon.vue'
import CheckIcon from '@/components/icons/CheckIcon.vue'
import DailyRewardModal from '@/components/modals/DailyRewardModal.vue'
import { type EcommerceItem, SPECIAL_ECOMMERCE_ITEM_ID } from '@/types/shared/player-config'
import { useConfetti } from '@/composables/game/confetti'

const { config } = useConfig()
const { player, checkin, hasCheckedInToday } = usePlayer()
const { startCoinAnimation } = useFlyingCoins()
const { getDayPoints, getDaySpins, isGoldenDay, isExtraGoldenDay, isBigGoldenDay } = useDailyReward()
const { nowDateTimestamp } = useNow()
const { showConfetti } = useConfetti()

const checkinEcommerceItem = computed<EcommerceItem | undefined>(() => {
  const item = config.value.game.ecommerce.items.find(item => item.id === SPECIAL_ECOMMERCE_ITEM_ID.DAILY_CHECKIN)
  if (item) {
    return item
  }
  return undefined
})

const lastCheckinDate = computed(() => DateTime.fromISO(player.value.tasks.dailyCheckin.lastCheckin, { zone: 'utc' }))
const rewardDaysCount = computed(() => config.value.game.tasks.dailyCheckin.rewardsV2.length)
const consecutiveDays = computed(() => player.value.tasks.dailyCheckin.consecutiveDays)
const dayDiffFromLastCheckin = computed(() => DateTime.fromSeconds(nowDateTimestamp.value, { zone: 'utc' }).diff(lastCheckinDate.value).as('days'))
const showRewardAfterLastDay = computed(() => consecutiveDays.value > config.value.game.tasks.dailyCheckin.rewardsV2.length - (hasCheckedInToday.value ? 0 : 1))
const extraReward = computed(() => getDayPoints(hasCheckedInToday.value ? consecutiveDays.value - 1 : consecutiveDays.value))
const isTodayConsecutive = computed(() => dayDiffFromLastCheckin.value <= (consecutiveDays.value >= 20 ? 2 : 1))

const showDailyRewardModal = ref<boolean>(false)
const dayToClaim = ref<number>(0)

function isDayDisabled(dayIndex: number) {
  if (dayIndex === 0) return false
  if (!player.value.tasks.dailyCheckin.lastCheckin) {
    return dayIndex !== 0
  }

  if (dayDiffFromLastCheckin.value <= 0) {
    return true
  }
  if (!isTodayConsecutive.value) {
    return dayIndex !== 0
  }

  return consecutiveDays.value !== dayIndex
}

function isDayCompleted(dayIndex: number) {
  if (!player.value.tasks.dailyCheckin.lastCheckin) {
    return false
  }
  return consecutiveDays.value > dayIndex && isTodayConsecutive.value
}

function isNextDay(dayIndex: number) {
  return (isDayCompleted(dayIndex - 1) && !isDayCompleted(dayIndex) && isDayDisabled(dayIndex))
}

async function claim(index: number) {
  dayToClaim.value = index
  showDailyRewardModal.value = true
}

async function onCheckin(nonce?: string) {
  try {
    showDailyRewardModal.value = false
    await checkin(nonce)
  } catch (e) {
    console.error(e)
  }
}

function handlePurchase(result: { paid: boolean, nonce?: string }) {
  if (result.paid) {
    onCheckin(result.nonce)
  }
}

watch(hasCheckedInToday, () => {
  if (hasCheckedInToday.value) {
    if (getDaySpins(consecutiveDays.value - 1)) {
      showConfetti()
    } else {
      startCoinAnimation()
    }
  }
})

</script>
<template>
  <div
    class="h-full flex flex-col items-center justify-between text-center overflow-auto px-4 pt-2.5 pb-6 gap-y-9"
  >
    <div class="h-full w-full flex flex-col items-center">
      <img
        src="@/assets/images/gifs/calendar.gif"
        class="size-[120px] mb-6"
      >
      <h2 class="bounce-in-right text-light-white text-2xl font-bold -tracking-[0.48px]">
        <T
          key-name="tasks.daily_reward.title"
          default-value="Daily reward"
        />
      </h2>
      <p class="bounce-in-left font-medium text-base text-white/70 mb-6 whitespace-pre-line">
        <T
          key-name="tasks.daily_reward.description"
          :default-value="`Earn coins daily for logging in.\nKeep your streak to earn more!`"
        />
      </p>

      <div
        v-if="showRewardAfterLastDay"
        class="w-full flex flex-col gap-y-8 items-center text-white mb-6"
      >
        <div
          :class="twMerge( 'flex flex-col gap-y-3 items-center pt-3 rounded-lg bg-dark-top-layer relative overflow-hidden w-full pointer-events-none',
                           !hasCheckedInToday && 'green-shadow pointer-events-auto scale-up',
          )"
          @click="() => claim(consecutiveDays)"
        >
          <div class="flex flex-col items-center gap-y-1">
            <div
              v-if="hasCheckedInToday"
              class="absolute top-0.5 right-0.5 p-1 z-[2]"
            >
              <CheckIcon class="text-teal-primary size-4" />
            </div>
            <img
              :src="CoinIcon"
              class="w-auto h-[42px]"
              alt="Coin Icon"
            >
            <div
              class="text-teal-primary text-base font-semibold -tracking-[0.32px]"
              v-tooltip="{content: formatUnshortenedNumber(extraReward), placement: 'bottom'}"
            >
              {{ formatNumberSemiTruncate(extraReward) }}
            </div>
            <div class="text-sm font-semibold -tracking-[0.28px] text-white/[.87]">
              <T
                key-name="tasks.daily_reward.x_days_streak"
                default-value="Amazing! You’re on a {day}-day streak!"
                :params="{day: consecutiveDays}"
              />
            </div>
            <i
              v-if=" !hasCheckedInToday"
              class="shine w-[25%]"
            />
          </div>
          <div
            :class="twMerge('bg-dark-top-most-layer flex items-center justify-center w-full text-white/[87%] z-[2]',
                            !hasCheckedInToday && 'bg-teal-primary')
            "
          >
            <p
              class="text-xs leading-6 font-bold"
            >
              <span
                v-if="!hasCheckedInToday"
                class="uppercase text-tropee-black"
              >
                <T
                  key-name="tasks.daily_reward.claim"
                  default-value="Claim"
                />
              </span>
              <span
                v-else
                class="uppercase"
              >
                <T
                  key-name="tasks.daily_reward.come_back"
                  default-value="COME BACK TOMORROW"
                />
              </span>
            </p>
          </div>
        </div>
        <div class="text-center text-lg font-semibold text-white">
          <T
            key-name="tasks.daily_reward.claim_further_title"
            default-value="Keep your streak alive!"
          />
        </div>
      </div>

      <div class="flex h-full w-full overflow-visible">
        <div class="h-full grid grid-cols-4 w-full justify-between gap-3 p-0.5 overflow-visible">
          <div
            v-for="dayIndex in [...Array(rewardDaysCount).keys()]"
            :key="dayIndex"
            :class="twMerge('w-full max-h-[109px] h-full aspect-[90/109] justify-center items-center rounded-lg pointer-events-none',
                            isDayDisabled(dayIndex) && 'pointer-events-none',
                            !isDayDisabled(dayIndex) && !isDayCompleted(dayIndex) && 'green-shadow pointer-events-auto scale-up',
                            isBigGoldenDay(dayIndex) && 'col-span-2 aspect-auto'
            )"
            @click="() => claim(dayIndex)"
          >
            <div
              :class="twMerge('flex flex-col justify-center items-center rounded-lg overflow-hidden w-full h-full pt-2.5 gap-y-1.5 bg-dark-top-layer relative',
                              isDayDisabled(dayIndex) && !isDayCompleted(dayIndex) && 'opacity-[68%]',
                              isGoldenDay(dayIndex) && 'golden-gradient-background',
                              isExtraGoldenDay(dayIndex) && 'extra-gold-gradient-background',
                              isBigGoldenDay(dayIndex) && 'items-start'

              )"
            >
              <i
                v-if="!isDayDisabled(dayIndex) && !isDayCompleted(dayIndex)"
                class="shine w-[35%]"
              />
              <div
                v-if="isDayCompleted(dayIndex)"
                class="absolute top-0 right-0 p-1 z-[2]"
              >
                <CheckIcon class="text-teal-primary size-4" />
              </div>
              <div
                v-if="isBigGoldenDay(dayIndex)"
                class="absolute bottom-0 left-1/3 size-[164px]"
              >
                <img
                  :src="SpinImage"
                  class="object-cover object-center"
                >
              </div>
              <div
                v-if="isExtraGoldenDay(dayIndex) && isBigGoldenDay(dayIndex)"
                :class="twMerge('flex flex-col items-center justify-between gap-y-1 h-full flex-1 text-white text-base font-semibold tracking-tight pl-3.5 z-[2]',
                                isNextDay(dayIndex) && 'text-white pulse-white',
                                !isDayDisabled(dayIndex) && !isDayCompleted(dayIndex) && 'text-white')"
              >
                <div
                  class="flex items-center gap-x-1 w-full"
                >
                  <img
                    :src="CoinIcon"
                    class="h-4 min-w-[10px] w-auto"
                    alt="Coin Icon"
                  >
                  <span>{{
                    formatNumberFullTruncate(getDayPoints(dayIndex))
                  }}</span>
                </div>
                <PlusIcon
                  class="size-2"
                />
                <p>
                  <T
                    key-name="fortune_wheel.info.more_spins.count_spin"
                    default-value="{count, plural, one {# spin} other {# spins}}"
                    :params="{count: getDaySpins(dayIndex)}"
                  />
                </p>
              </div>
              <div
                v-else-if="isBigGoldenDay(dayIndex)"
                :class="twMerge('flex flex-col justify-center h-full flex-1 pl-3 font-semibold text-white', !isDayDisabled(dayIndex) && !isDayCompleted(dayIndex) && 'text-white',
                                isNextDay(dayIndex) && 'text-white pulse-white')"
              >
                <p class="text-[38px] leading-[38px] -tracking-[0.76px]">
                  {{ getDaySpins(dayIndex) }}
                </p>
                <p class="text-base tracking-tight">
                  <T
                    v-if="isGoldenDay(dayIndex)"
                    key-name="fortune_wheel.info.more_spins.count_spin"
                    :default-value="`{count, plural, one {spin} other {spins}}`"
                    :params="{count: getDaySpins(dayIndex)}"
                  />
                </p>
              </div>
              <template v-else>
                <div class="flex h-full w-full items-center justify-center overflow-hidden mb-1">
                  <img
                    v-if="isGoldenDay(dayIndex)"
                    :src="SpinImage"
                    class="h-full w-auto max-h-full"
                  >
                  <img
                    v-else
                    :src="CoinIcon"
                    class="h-full"
                    alt="Coin Icon"
                  >
                </div>
                <p
                  :class="twMerge('text-base font-semibold text-white tracking-tight', !isDayDisabled(dayIndex) && !isDayCompleted(dayIndex) && 'text-teal-primary',
                                  isNextDay(dayIndex) && 'text-teal-primary pulse')"
                >
                  <T
                    v-if="isGoldenDay(dayIndex)"
                    key-name="fortune_wheel.info.more_spins.count_spin"
                    default-value="{count, plural, one {# spin} other {# spins}}"
                    :params="{count: getDaySpins(dayIndex)}"
                  />
                  <template
                    v-else
                  >
                    {{ formatNumberFullTruncate(getDayPoints(dayIndex)) }}
                  </template>
                </p>
              </template>
              <div
                :class="twMerge('bg-dark-top-most-layer flex items-center justify-center w-full text-white/[87%] z-[2]',
                                isGoldenDay(dayIndex) && (isDayDisabled(dayIndex) || isDayCompleted(dayIndex)) && 'dark-gold-gradient-background text-tropee-black',
                                !isDayDisabled(dayIndex) && !isDayCompleted(dayIndex) && 'bg-teal-primary')
                "
              >
                <p
                  class="text-xs leading-6 font-bold "
                >
                  <span
                    v-if="!isDayDisabled(dayIndex) && !isDayCompleted(dayIndex)"
                    class="uppercase text-tropee-black"
                  >
                    <T
                      key-name="tasks.daily_reward.claim"
                      default-value="Claim"
                    />
                  </span>
                  <span v-else>
                    <T
                      key-name="tasks.daily_reward.day"
                      default-value="Day"
                    />
                    {{ dayIndex + 1 }}
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <DailyRewardModal
      v-if="checkinEcommerceItem && showDailyRewardModal"
      :show="showDailyRewardModal"
      :points="getDayPoints(dayToClaim)"
      :spins="getDaySpins(dayToClaim)"
      :ecommerce-item="checkinEcommerceItem"
      @hide="showDailyRewardModal = false"
      @on-collect1x="onCheckin"
      @purchase="handlePurchase"
    />
  </div>
</template>
<style scoped lang="scss">
.green-linear {
  background: linear-gradient(to right, rgba(254, 78, 254, 0.75), rgba(20, 241, 255, 0.75) 33%);
}

.golden-gradient-background {
  background: linear-gradient(105deg,
    rgba(255, 203, 0, 0.52) 10.46%,
    rgba(181, 120, 0, 0.52) 61.24%,
    rgba(183, 124, 7, 0.52) 70.47%,
    rgba(191, 136, 27, 0.52) 82.01%,
    rgba(203, 157, 60, 0.52) 95.86%,
    rgba(220, 185, 106, 0.52) 112.01%,
    rgba(241, 221, 165, 0.52) 130.48%,
    rgba(255, 244, 201, 0.52) 139.71%,
    rgba(234, 206, 136, 0.52) 153.55%,
    rgba(216, 173, 78, 0.52) 167.4%,
    rgba(203, 148, 35, 0.52) 178.94%,
    rgba(195, 133, 9, 0.52) 188.17%,
    rgba(192, 128, 0, 0.52) 192.79%,
    rgba(197, 133, 0, 0.52) 197.41%,
    rgba(234, 178, 0, 0.52) 227.41%,
    rgba(249, 195, 0, 0.52) 241.26%
  );
}

.extra-gold-gradient-background {
  background: linear-gradient(105deg,
    #FFCB00 10.46%,
    #B57800 61.24%,
    #B77C07 70.47%,
    #BF881B 82.01%,
    #CB9D3C 95.86%,
    #DCB96A 112.01%,
    #F1DDA5 130.48%,
    #FFF4C9 139.71%,
    #EACE88 153.55%,
    #D8AD4E 167.4%,
    #CB9423 178.94%,
    #C38509 188.17%,
    #C08000 192.79%,
    #C58500 197.41%,
    #EAB200 227.41%,
    #F9C300 241.26%
  );
}

.dark-gold-gradient-background {
  background: linear-gradient(105deg,
    #FFCB00 10.46%,
    #B57800 61.24%,
    #B77C07 70.47%,
    #BF881B 82.01%,
    #CB9D3C 95.86%,
    #DCB96A 112.01%,
    #F1DDA5 130.48%,
    #FFF4C9 139.71%,
    #EACE88 153.55%,
    #D8AD4E 167.4%,
    #CB9423 178.94%,
    #C38509 188.17%,
    #C08000 192.79%,
    #C58500 197.41%,
    #EAB200 227.41%,
    #F9C300 241.26%
  );
}

.green-shadow {
  box-shadow: 0px 0px 24px 0px #14F1FF;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    filter: drop-shadow(0px 0px 0px rgba(20, 241, 255, 0)); /* No shadow */
  }
  50% {
    transform: scale(1.03);
    filter: drop-shadow(0px 0px 6px #14F1FFC6); /* Full shadow */
  }
  100% {
    transform: scale(1); /* Back to original size */
    filter: drop-shadow(0px 0px 6px rgba(20, 241, 255, 0));
  }
}

.pulse {
  animation: pulse 2s infinite;
  will-change: transform, filter;
}

@keyframes pulse-white {
  0% {
    transform: scale(1);
    filter: drop-shadow(0px 0px 0px rgba(255, 255, 255, 0)); /* No shadow */
  }
  50% {
    transform: scale(1.03);
    filter: drop-shadow(0px 0px 6px #FFF); /* Full shadow */
  }
  100% {
    transform: scale(1); /* Back to original size */
    filter: drop-shadow(0px 0px 6px rgba(255, 255, 255, 0));
  }
}

.pulse-white {
  animation: pulse-white 2s infinite;
  will-change: transform, filter;
}

.scale-up {
  animation: scale-up 0.75s ease infinite alternate;
}

@keyframes scale-up {
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.03);
  }
}
</style>
